import React from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { useSiteSelectorContext } from "@/components/site-selector-modal";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";

export type TemplateCardProps = {
    icon: React.ReactNode;
    title: string;
    name: string;
    description: string;
    try: string;
    templateId: string;
    learnMoreLink: string;
    learnMoreLabel: string;
    barColor: string;
};

const cardStyles = css({
    backgroundColor: "#fff",
    borderRadius: "3px",
    boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)",
    borderBlockStartWidth: "10px",
    borderBlockStartStyle: "solid",
    marginBlockEnd: token("space.100"),
    padding: "32px",
    textAlign: "left",
    width: "100%"
});

const cardIconStyles = css({
    borderRadius: "50%",
    padding: token("space.200"),
    width: "64px",
    height: "64px",
    marginBlockEnd: token("space.200")
});

const cardHeading3Styles = css({
    "color": "#253858",
    "display": "block",
    "fontSize": "1.5rem",
    "fontWeight": "500",
    "lineHeight": token("space.400"),
    "paddingBlockEnd": token("space.200"),
    "margin": token("space.0"),
    "@media not all and (min-width: 64rem)": {
        fontSize: "1.25rem"
    }
});

const cardParagraphStyles = css({
    fontSize: token("space.200"),
    fontWeight: "400",
    lineHeight: token("space.250"),
    marginBlockEnd: token("space.300")
});

const cardAnchorStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a:hover": {
        textShadow: "none",
        textDecoration: "underline"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a:focus": {
        textShadow: "none",
        textDecoration: "underline"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".try-link": {
        marginBlockEnd: "24px"
    }
});

const TemplateCard = (props: TemplateCardProps) => {
    const {
        icon,
        title,
        name,
        description,
        try: tryText,
        templateId,
        learnMoreLink,
        learnMoreLabel,
        barColor
    } = props;
    const { openModalForTemplate } = useSiteSelectorContext();

    const handleClicked = (event: React.MouseEvent<Element, MouseEvent>) => {
        event.preventDefault();
        openModalForTemplate({
            templateId,
            templateName: name,
            productKey: ProductKeys.JIRA_SERVICE_DESK
        });
    };

    return (
        <div
            style={{ borderTopColor: barColor || "transparent" }}
            css={cardStyles}
        >
            <div
                style={{ backgroundColor: barColor || "transparent" }}
                css={cardIconStyles}
            >
                {icon}
            </div>
            <h3 css={cardHeading3Styles}>{title}</h3>
            <p css={cardParagraphStyles}>{description}</p>
            <Link
                href={""}
                onClick={handleClicked}
                text={tryText}
                type="external"
                className="try-link"
                css={cardAnchorStyles}
            />
            <Link
                href={useLocalizedHref(learnMoreLink)}
                text={learnMoreLabel}
                type="primary"
                css={cardAnchorStyles}
            />
        </div>
    );
};

export default TemplateCard;
